import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { Currency, CurrencyListResponse, CurrencyCreate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.tsx';

const PAGE_SIZE = 10;

interface UseCurrenciesResult {
  currencies: Currency[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number; // Added totalItems to the result interface
  fetchCurrencies: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createCurrency: (data: CurrencyCreate) => Promise<boolean>;
  updateCurrency: (id: number, data: CurrencyCreate) => Promise<boolean>;
  deleteCurrency: (id: number) => Promise<boolean>;
}

export const useCurrencies = (): UseCurrenciesResult => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // New state for totalItems
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrencies = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<CurrencyListResponse>(
        `/currencies?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setCurrencies(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items); // Set totalItems from response
      // REMOVED: setCurrentPage(response.pagination.page); // Ensure currentPage state matches fetched page
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch currencies: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching currencies.');
      }
      setCurrencies([]);
      setTotalItems(0); // Reset totalItems on error
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchCurrencies(currentPage);
  }, [currentPage, fetchCurrencies]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createCurrency = async (data: CurrencyCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Currency>('/currencies', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      //alert('Currency created successfully!');
      await fetchCurrencies(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create currency: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateCurrency = async (id: number, data: CurrencyCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Currency>(`/currencies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      //alert('Currency updated successfully!');
      await fetchCurrencies(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update currency: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteCurrency = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Currency ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/currencies/${id}`, { method: 'DELETE' });
      //alert(`Currency ${id} deleted successfully!`);
      const pageToFetch = currencies.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchCurrencies(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete currency: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    currencies,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems, // Return totalItems
    fetchCurrencies,
    handlePageChange,
    createCurrency,
    updateCurrency,
    deleteCurrency,
  };
};